import React, {Fragment, useEffect, useRef, useState} from "react";
import {Badge, Card, CardBody, Col, Alert, Modal, ModalBody} from "reactstrap";
import {Button} from "react-bootstrap";
import DropZone from "../../../../../share-components/upload/dropzone";
import {useDispatch} from "react-redux";
import dragula from 'react-dragula';
import {
    addProductImage,
    deleteProductImage,
    setImagePartners,
    sortProductImage,
    updateProductImageAlt
} from "../../../store/prodcutSlice";
import {getFileExtension} from "../../../../../utils/common";
import ReactImageVideoLightbox from "react-image-video-lightbox";
import {toast} from "react-toastify";

const isVideo = (item) => getFileExtension(item.image_path) == ".mp4";
const partnerIdsOf = (item) => (item.partners || []).map(p => p.id);

const TickIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 6.5l2.6 2.5L10 3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);
const TagIcon = () => (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M2 2h6l6 6-6 6-6-6z" stroke="#7366ff" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="5.5" cy="5.5" r="1.2" fill="#7366ff"/>
    </svg>
);

const ProductImage = (props) => {
    const {productState, setValue, trigger, getValues,onConfirmSendImage,currentUser} = props;
    const dispatch = useDispatch();
    let imageAltRef = useRef([]);

    const [imagePreloader, setImageLoader] = useState(false);
    const [isUpdated, setIsUpdated] = useState(false);

    const [viewFile, setViewFile] = useState([]);
    const [dragulaKey, setDragulaKey] = useState(0);

    const dragulaRef = useRef(null);
    const dragulaInstance = useRef(null);

    // --- partner assignment (an image with no partner is a normal Goddiva image) ---
    // Only on the primary seller's products (the backend decides); other sellers keep the plain Images section.
    const partnersEnabled = !!(productState && productState.image_partners_enabled);
    const flaggedPartners = partnersEnabled && productState.image_partners ? productState.image_partners : [];
    const [selected, setSelected] = useState({});
    const [activeTab, setActiveTab] = useState('all');
    const [assignOpen, setAssignOpen] = useState(false);
    const [assignFor, setAssignFor] = useState([]);
    const [assignSearch, setAssignSearch] = useState('');
    const [ticked, setTicked] = useState({});
    const [saving, setSaving] = useState(false);
    const [infoImageId, setInfoImageId] = useState(null);
    // Partner changes are saved at once, but the Goddiva UK store only changes on the next Save & Publish.
    const [pendingPublish, setPendingPublish] = useState(false);

    const images = getValues('images') || [];
    const partnerMap = {};
    const counts = {};
    images.forEach(img => (img.partners || []).forEach(p => {
        partnerMap[p.id] = p;
        counts[p.id] = (counts[p.id] || 0) + 1;
    }));
    const taggedPartners = Object.values(partnerMap).sort((a, b) => a.name.localeCompare(b.name));
    const anyTagged = partnersEnabled && taggedPartners.length > 0;
    const tab = anyTagged && (activeTab === 'all' || activeTab === 'goddiva' || partnerMap[activeTab]) ? activeTab : 'all';
    const activePartner = tab !== 'all' && tab !== 'goddiva' ? partnerMap[tab] : null;
    const visibleImages = tab === 'all'
        ? images
        : tab === 'goddiva'
            ? images.filter(img => partnerIdsOf(img).length === 0)
            : images.filter(img => partnerIdsOf(img).indexOf(activePartner.id) >= 0);
    const selectedIds = Object.keys(selected).map(Number).filter(id => images.some(img => img.id === id && !isVideo(img)));
    const infoImage = infoImageId ? images.find(img => img.id === infoImageId) : null;
    const tickedCount = Object.keys(ticked).filter(k => ticked[k] === true).length;
    const q = assignSearch.trim().toLowerCase();
    const assignPartners = flaggedPartners.filter(p => !q || p.name.toLowerCase().indexOf(q) >= 0);

    useEffect(() => {
        if (dragulaRef.current && !dragulaInstance.current) {
            dragulaInstance.current = dragula([dragulaRef.current], {
                // Never start a drag from the checkbox or the partner chip.
                moves: (el, source, handle) => !(handle && handle.closest && (handle.closest('.image-select') || handle.closest('.image-partners'))),
                accepts: (el, target, source, sibling) => true,
                invalid: (el, handle) => false,
            });

            dragulaInstance.current.on("drop", (el, target, source, sibling) => {
                // Dragging inside a tab reorders only that tab's photos, which keep the slots they
                // already occupy in the full list; every other photo stays where it is. The sort
                // endpoint renumbers exactly the ids it receives, so it always gets the full order.
                const visibleIds = [...target.children].map(item => Number(item.getAttribute("data-id"))).filter(Boolean);
                const visibleSet = new Set(visibleIds);
                const current = getValues('images') || [];
                let next = 0;
                const imageIds = current.map(img => visibleSet.has(img.id) ? visibleIds[next++] : img.id);
                const byId = {};
                current.forEach(img => { byId[img.id] = img; });
                const reordered = imageIds.map((id, index) => ({...byId[id], order: index + 1}));

                dispatch(sortProductImage({ id: getValues("id"), image_ids: imageIds })).then(res => {
                    if (!res.error) {
                        toast.success("Image order saved", {
                            position: toast.POSITION.TOP_RIGHT,
                        });
                    }
                });
                setValue('images', reordered);
                trigger('images');
                setDragulaKey(k => k + 1);
            });
        }

        return () => {
            if (dragulaInstance.current) {
                dragulaInstance.current.destroy();
                dragulaInstance.current = null;
            }
        };
    }, [getValues('images'), dispatch]);

    const validateImage = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = () => {
                const width = img.width;
                const height = img.height;
                const megapixels = (width * height) / 1_000_000; // Convert to MP

                if (megapixels > 25) {
                    reject("Image size should not exceed 25 megapixels.");
                } else {
                    resolve("Valid Image");
                }
            };
            img.onerror = () => reject("Invalid image file.");
        });
    };

    const onChangeFile = async (e, error) => {
        if(error.length>0){
            toast.error("one or many of the uploaded file size more than 20MB for image and 100MB for video", {
                position: toast.POSITION.TOP_RIGHT,
            });
        }else {
            if (e.length > 0) {
                let fileSizeError = false;
                let validationErrors = [];

                for (const file of e) {
                    if (/^image\//.test(file.type) && file.size > 20971520) {
                        fileSizeError = true;
                    } else if (file.type === 'video/mp4' && file.size > 104857600) {
                        fileSizeError = true;
                    }

                    if (/^image\//.test(file.type)) {
                        try {
                            await validateImage(file);
                        } catch (error) {
                            validationErrors.push(error);
                        }
                    }
                }

                if (fileSizeError || validationErrors.length > 0) {
                    toast.error(
                        fileSizeError
                            ? "One or more files exceed size limits (20MB for images, 100MB for videos)."
                            : validationErrors.join(", "),
                        { position: toast.POSITION.TOP_RIGHT }
                    );
                    return;
                }

                setImageLoader(true);
                dispatch(addProductImage({
                    id: getValues('id'),
                    image: e,
                    shopify_product_id: getValues('shopify_product_id'),
                    // Uploading inside a partner tab reserves the whole batch for that partner.
                    partner_ids: activePartner ? [activePartner.id] : []
                })).then(res => {
                    if (!res.error) {
                        setValue('images', res.payload);
                        trigger('images');
                        setDragulaKey(dragulaKey + 1);
                        setImageLoader(false);
                    }
                    ;
                });
            }
        }
    };

    const deleteImage = (item) => {
        dispatch(deleteProductImage(item.id)).then(res => {
            if (!res.error) {
                setValue('images', res.payload);
                trigger('images');
                setDragulaKey(dragulaKey + 1);
            }
        });
    }

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = {...prev};
            if (next[id]) delete next[id]; else next[id] = true;
            return next;
        });
    };
    const clearSelection = () => setSelected({});
    const switchTab = (key) => {
        setActiveTab(key);
        setSelected({});
    };

    // Partners already on every given image are ticked; on some of them = 'mixed' (left alone unless changed).
    const preselect = (ids) => {
        const t = {};
        flaggedPartners.forEach(p => {
            const on = ids.filter(id => {
                const img = images.find(i => i.id === id);
                return img && partnerIdsOf(img).indexOf(p.id) >= 0;
            }).length;
            if (ids.length && on === ids.length) t[p.id] = true;
            else if (on > 0) t[p.id] = 'mixed';
        });
        return t;
    };
    const openAssign = (ids) => {
        setAssignFor(ids);
        setTicked(preselect(ids));
        setAssignSearch('');
        setAssignOpen(true);
    };
    const toggleTick = (pid) => setTicked(prev => ({...prev, [pid]: prev[pid] === true ? false : true}));

    // One request per distinct resulting partner set (the endpoint sets exactly the given partners).
    const applyPartnerSets = async (targets) => {
        const groups = {};
        targets.forEach(t => {
            const key = t.partnerIds.slice().sort((a, b) => a - b).join(',');
            if (!groups[key]) groups[key] = {partnerIds: t.partnerIds, imageIds: []};
            groups[key].imageIds.push(t.id);
        });
        setSaving(true);
        let payload = null;
        for (const group of Object.values(groups)) {
            const res = await dispatch(setImagePartners({
                product_id: getValues('id'),
                image_ids: group.imageIds,
                partner_ids: group.partnerIds
            }));
            if (res.error) {
                setSaving(false);
                return false;
            }
            payload = res.payload;
        }
        setSaving(false);
        if (payload) {
            setValue('images', payload);
            trigger('images');
            setDragulaKey(k => k + 1);
        }
        setPendingPublish(true);
        toast.success("Image partners saved. Click Save & Publish to update the Goddiva UK store.", {
            position: toast.POSITION.TOP_RIGHT,
        });
        return true;
    };

    const assign = async () => {
        const add = Object.keys(ticked).filter(k => ticked[k] === true).map(Number);
        const remove = Object.keys(ticked).filter(k => ticked[k] === false).map(Number);
        const targets = assignFor.map(id => {
            const img = images.find(i => i.id === id);
            const current = img ? partnerIdsOf(img) : [];
            return {
                id,
                partnerIds: current.filter(pid => remove.indexOf(pid) < 0).concat(add.filter(pid => current.indexOf(pid) < 0))
            };
        });
        if (await applyPartnerSets(targets)) {
            setAssignOpen(false);
            setSelected({});
            setInfoImageId(null);
        }
    };

    const removeFromTab = async () => {
        if (!activePartner) return;
        const targets = selectedIds.map(id => {
            const img = images.find(i => i.id === id);
            return {id, partnerIds: partnerIdsOf(img).filter(pid => pid !== activePartner.id)};
        });
        if (await applyPartnerSets(targets)) setSelected({});
    };

    const initilindex = {index: 0, isOpen: false}
    const [photoIndex, setPhotoIndex] = useState(initilindex)
    const [selectedColour,setSelectedColour]=useState(0);

    const onMoveNext = (index) => {
        imageAltRef.current.value = getValues('images')[index].image_alt;
        setSelectedColour(getValues('images')[index].colour_id);
    }
    const updateImageAltText = (index) => {
        let imageText = imageAltRef.current;
        getValues('images')[photoIndex.index].image_alt = imageText.value;
        setIsUpdated(true);

    }
    const setImageColour=(index,item)=>{
        getValues('images')[photoIndex.index].colour_id = item.id;
        getValues('images')[photoIndex.index].colour = item;
        setSelectedColour(item.id);
        setIsUpdated(true);
    }
    const updateAltTagWithColour=()=>{
        if(isUpdated) {
            const images = getValues('images').map(elem => (
                {
                    id: elem.id,
                    colour_id: elem.colour_id,
                    image_alt:elem.image_alt
                }
            ));
            dispatch(updateProductImageAlt({images:images})).then(res => {
                if (!res.error) {
                   setIsUpdated(false);
                }
            });
        }

    }
    useEffect(()=>{
        if(photoIndex.isOpen) {
            setSelectedColour(getValues('images')[photoIndex.index].colour_id);
            imageAltRef.current.value = getValues('images')[photoIndex.index].image_alt;
        }
    },[photoIndex.index,photoIndex.isOpen])
    useEffect(() => {
        let dataImage = [];
        getValues('images').map((item, i) => {
            dataImage.push({
                url: item.image_path,
                type: getFileExtension(item.image_path) == ".mp4" ? "video" : "photo",
                altTag: "some image",
            });
        });
        setViewFile(dataImage);
    }, [getValues('images')])

    const tabs = anyTagged ? [
        {key: 'all', label: 'All images', count: images.length},
        {key: 'goddiva', label: 'Goddiva', count: images.filter(img => partnerIdsOf(img).length === 0).length},
    ].concat(taggedPartners.map(p => ({key: p.id, label: p.name, count: counts[p.id]}))) : [];
    const tabHint = activePartner
        ? `Images shown here are sent only to ${activePartner.name}. Select images to remove them from this partner.`
        : tab === 'goddiva'
            ? 'Normal images: the Goddiva store and every feed.'
            : 'Every image. A chip under a photo means it goes only to those partners.';

    return (
        <Fragment>
            <Card>
                <CardBody>
                    <div className="ribbon ribbon-clip ribbon-warning ribbon-left mb-5">Images</div>

                    <div className="form-row mb-3 mt-3">
                        <div className="col-4 offset-8">
                            <Alert color="warning outline">
                                <span>{"Videos can be uploaded after publish"}</span>
                            </Alert>
                        </div>
                    </div>

                    {pendingPublish && <Alert color="primary" className="image-publish-note">
                        Partner changes are saved, but the Goddiva UK store is not updated yet. Click <strong>Save &amp; Publish</strong> to apply them. The US store updates automatically.
                    </Alert>}

                    {anyTagged && <Fragment>
                        <div className="image-tabs">
                            {tabs.map(t => <div key={t.key} className={`image-tab ${tab === t.key ? 'active' : ''}`} onClick={() => switchTab(t.key)}>
                                {t.label}<span className="image-tab-count">{t.count}</span>
                            </div>)}
                        </div>
                        <div className="image-tab-hint">{tabHint}</div>
                    </Fragment>}

                    {selectedIds.length > 0 && <div className="image-selection-bar">
                        <span className="font-weight-bold">{selectedIds.length} {selectedIds.length === 1 ? 'image' : 'images'} selected</span>
                        <span className="divider"></span>
                        <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => openAssign(selectedIds)}>Assign to partners</button>
                        {activePartner && <button type="button" className="btn btn-outline-danger btn-sm" disabled={saving} onClick={removeFromTab}>Remove from {activePartner.name}</button>}
                        <span className="flex-grow-1"></span>
                        <span className="image-link" onClick={clearSelection}>Clear selection</span>
                    </div>}

                    <div className="form-row mb-3">

                        <div ref={dragulaRef} style={{display: "contents"}} key={dragulaKey}>
                            {visibleImages.map((item, i) => <Col md={2} index={i} data-id={item.id} key={item.id}>


                                <Card className={selected[item.id] ? 'selected-image' : ''}>
                                    <div className="product-box">
                                        {/* Above .product-img on purpose: the hover overlay covers that box entirely. */}
                                        <div className="image-card-head">
                                            {/* Display only. The colour is chosen in the lightbox (eye icon); the lightbox handler
                                                must not run from here, where `item` is the image and no photo is open. */}
                                            <Badge className="image-colour-label badge bg-light text-dark">{item.colour?item.colour.name:"None"}</Badge>
                                            {/* Videos are always normal (they reach Shopify at upload time), so they cannot be selected for a partner. */}
                                            {partnersEnabled && !isVideo(item) && <div className="image-select" onMouseDown={(e) => e.stopPropagation()}>
                                                <span className={`image-check ${selected[item.id] ? 'checked' : ''}`} title="Select image" onClick={() => toggleSelect(item.id)}>
                                                    {selected[item.id] && <TickIcon/>}
                                                </span>
                                            </div>}
                                        </div>
                                        <div className="product-img">
                                            <div className="image-crop-center" style={{height: 200}}>
                                                {isVideo(item) ?
                                                    <video height="200" width={350} autoPlay={true} muted={true}
                                                           loop={true}>
                                                        <source src={item.image_path} type="video/mp4"/>
                                                    </video>
                                                    : <img className="img-fluid scale-image-6" src={item.image_path}
                                                           alt=""/>}
                                            </div>
                                            <div className="product-hover">
                                                <ul>
                                                    <li>
                                                        <Button color="default" title="View / edit image" onClick={() => {
                                                            setPhotoIndex({...photoIndex, index: images.indexOf(item), isOpen: true})
                                                        }}>
                                                            <i className="icon-eye"></i>
                                                        </Button>
                                                    </li>
                                                    <li>
                                                        <Button color="red" title="Delete image" onClick={() => {
                                                            deleteImage(item)
                                                        }}>
                                                            <i className="icon-trash"></i>
                                                        </Button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        {partnersEnabled && !isVideo(item) && <div className="image-partners" onMouseDown={(e) => e.stopPropagation()}>
                                            {(item.partners && item.partners.length > 0)
                                                ? <span className="partner-chip" title={'Sent only to: ' + item.partners.map(p => p.name).join(', ')} onClick={() => setInfoImageId(item.id)}>
                                                    <TagIcon/>{item.partners.length} {item.partners.length === 1 ? 'partner' : 'partners'}
                                                  </span>
                                                : <span className="image-goddiva" title="Goes to the Goddiva store and every feed">Goddiva</span>}
                                        </div>}
                                    </div>
                                </Card>


                            </Col>)}
                        </div>
                        {visibleImages.length === 0 && <Col md={12}><div className="image-tab-empty">No images here yet.</div></Col>}
                        <Col md={2}>
                            <Card>
                                <div className="product-box">
                                    <div className="product-img pt-3 pb-3">
                                        {imagePreloader && <div className="loader-box">
                                            <div className="loader-2"></div>
                                        </div>}
                                        {!imagePreloader && <DropZone multiple
                                            placeholder={activePartner ? `Upload here for ${activePartner.name} only` : undefined}
                                            extension={getValues('shopify_product_id') ? "image/*,.mp4" : "image/*"}
                                            onChangeFile={onChangeFile}></DropZone>}
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </div>
                    {(currentUser.modules).includes('send-image') &&   <div className="form-row">
                        <div className="col-4 offset-8">
                            <Button onClick={onConfirmSendImage} className="pull-right  font-weight-bold mr-2" color="success"
                                    type="button">Send Image
                            </Button>
                        </div>
                    </div>}
                </CardBody>
            </Card>

            <Modal isOpen={assignOpen} centered={true} toggle={() => setAssignOpen(false)} className="image-partners-modal">
                <ModalBody className="p-0">
                    <div className="modal-head">
                        <div className="modal-title-text">Assign to partners</div>
                        <div className="modal-subtitle">{assignFor.length} {assignFor.length === 1 ? 'image' : 'images'} selected. Partners already on {assignFor.length === 1 ? 'this image' : 'these images'} are ticked; tick to add, untick to remove.</div>
                    </div>
                    <div className="px-4 pb-2">
                        <input className="form-control" placeholder="Search partners" value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)}/>
                    </div>
                    <div className="partner-rows">
                        {assignPartners.map(p => <div key={p.id} className="partner-row" onClick={() => toggleTick(p.id)}>
                            <span className={`tri-check ${ticked[p.id] === true ? 'checked' : ticked[p.id] === 'mixed' ? 'mixed' : ''}`}>
                                {ticked[p.id] === true && <TickIcon/>}
                                {ticked[p.id] === 'mixed' && <span className="tri-dash"></span>}
                            </span>
                            <span className="partner-name">{p.name}</span>
                            {ticked[p.id] === 'mixed' && <span className="partner-meta">on some of the selected images</span>}
                        </div>)}
                        {assignPartners.length === 0 && <div className="partner-meta p-3">
                            {flaggedPartners.length === 0 ? 'No partner is marked "Needs different product images".' : 'No partner matches that name.'}
                        </div>}
                    </div>
                    <div className="partner-note px-4">Only partners with "Needs different product images" ticked are listed. Ticked = the image goes only to that partner. Untick everything to make it a normal Goddiva image.</div>
                    <div className="modal-actions">
                        <span className="image-link" onClick={() => setAssignOpen(false)}>Cancel</span>
                        <button type="button" className="btn btn-primary" disabled={saving} onClick={assign}>
                            {saving ? 'Saving...' : tickedCount ? `Assign to ${tickedCount} ${tickedCount === 1 ? 'partner' : 'partners'}` : 'Save as normal (Goddiva)'}
                        </button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={!!infoImage} centered={true} size="lg" toggle={() => setInfoImageId(null)} className="image-partners-modal">
                {infoImage && <ModalBody className="p-0 d-flex">
                    <div className="image-info-photo">
                        <img src={infoImage.image_path} alt=""/>
                        <span className="badge bg-light text-dark">{infoImage.colour ? infoImage.colour.name : 'None'}</span>
                    </div>
                    <div className="image-info-body">
                        <div className="modal-title-text">Sent only to</div>
                        <div className="modal-subtitle">{infoImage.partners.length} {infoImage.partners.length === 1 ? 'partner' : 'partners'}</div>
                        <div className="partner-list mt-3">
                            {infoImage.partners.map(p => <div key={p.id} className="partner-list-row"><span className="partner-dot"></span>{p.name}</div>)}
                        </div>
                        <div className="partner-note mt-3">This image is not on the Goddiva store or in any feed. It goes only to the partners above.</div>
                        <div className="flex-grow-1"></div>
                        <div className="modal-actions px-0">
                            <span className="image-link" onClick={() => setInfoImageId(null)}>Close</span>
                            <button type="button" className="btn btn-primary" onClick={() => {
                                setSelected({[infoImage.id]: true});
                                setInfoImageId(null);
                                openAssign([infoImage.id]);
                            }}>Change partners</button>
                        </div>
                    </div>
                </ModalBody>}
            </Modal>

            {photoIndex.isOpen && (
                <div style={{height: '100vh'}}>
                    <div className="lightbox-div">
                        <ReactImageVideoLightbox
                            title={"DAd"}
                            data={viewFile}
                            startIndex={photoIndex.index}
                            showResourceCount={true}
                            onCloseCallback={() => {
                                updateAltTagWithColour();
                                setPhotoIndex({...photoIndex, isOpen: false})
                            }}
                            onNavigationCallback={(currentIndex) =>{

                                setPhotoIndex({...photoIndex, index: currentIndex})
                                onMoveNext(currentIndex);
                            }}
                        >

                        </ReactImageVideoLightbox>
                        <div className="p-5" style={{zIndex: 300, bottom: 0, position: "fixed", right: 0}}>
                            <div>
                                <input name="altText" type="text"
                                       placeholder="Alt Text"
                                       ref={(ref)=>{
                                           imageAltRef.current=ref;
                                       }}
                                       onBlur={(e) => {
                                           updateImageAltText(photoIndex.index);
                                       }}
                                       className={`form-control`}/>
                            </div>
                            <div className="pt-2">
                                    {getValues('color').map((item, i) => <Badge key={i} onClick={()=>{
                                        setImageColour(photoIndex.index,item);
                                    }}  className={`image-color badge bg-light text-dark ${selectedColour == item.id?`active-image-color`:""}`}>{item.name}</Badge>)}

                            </div>
                        </div>
                    </div>

                </div>
            )}

        </Fragment>
    )
};

export default ProductImage;
